import React, { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { useHotkeys } from 'react-hotkeys-hook';

import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getFoldersThunk } from '../../../store/folders/foldersSlice';
import { saveMyOrganization } from '../../../store/user/userSlice';
import { deleteVideofeed } from '../../../api/videofeeds';
import styles from './DeleteVideofeedModal.module.scss';
import { NOTIFICATION } from '../../../enums/ENUMS';
import { getUserOrganization } from '../../../api';
import Modal from '../../modules/Modal';

type TDeleteVideofeedModal = {
  videofeedId: string;
  open: boolean;
  duplicate?: boolean;
  hasReferenceTimelines?: boolean;
  deleteCollection?: boolean;
  deleteTargetLabel?: 'timeline' | 'video' | 'composer';
  switchModal: () => void;
  fetchAll?: () => void;
  clearLocalStorageData?: () => void;
};

const DeleteVideofeedModal: React.FC<TDeleteVideofeedModal> = ({
  videofeedId,
  duplicate = false,
  hasReferenceTimelines = false,
  deleteCollection = false,
  deleteTargetLabel = 'timeline',
  open,
  switchModal,
  fetchAll,
  clearLocalStorageData,
}) => {
  const notification = useSnackbar();
  const [checkingImpact, setCheckingImpact] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [impactedComposers, setImpactedComposers] = useState<string[]>([]);
  const dispatch = useAppDispatch();
  const submitButtonRef = useRef<HTMLDivElement>(null);
  const preflightRequestKeyRef = useRef<string | null>(null);
  const deleteInFlightRef = useRef(false);
  const switchModalRef = useRef(switchModal);
  const notificationRef = useRef(notification);
  const { page, limit, sortOption } = useAppSelector((state) => state.folderStore);

  useEffect(() => {
    switchModalRef.current = switchModal;
  }, [switchModal]);

  useEffect(() => {
    notificationRef.current = notification;
  }, [notification]);

  useEffect(() => {
    let mounted = true;

    if (!open) {
      preflightRequestKeyRef.current = null;
      deleteInFlightRef.current = false;
      setImpactedComposers([]);
      setCheckingImpact(false);
      setDeleting(false);
      return;
    }

    const requestKey = `${videofeedId}:${deleteCollection ? 'collection' : 'single'}`;
    if (preflightRequestKeyRef.current === requestKey) {
      return;
    }
    preflightRequestKeyRef.current = requestKey;

    setCheckingImpact(true);
    deleteVideofeed({
      videofeedId,
      deleteCollection,
    })
      .then((res) => {
        if (!mounted) return;
        setImpactedComposers(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        if (!mounted) return;
        // @ts-ignore
        if (err?.response?.data?.message) {
          // @ts-ignore
          notificationRef.current.showMessage(err?.response?.data?.message);
        } else {
          notificationRef.current.showMessage(NOTIFICATION.NOT_DELETED);
        }
        preflightRequestKeyRef.current = null;
        switchModalRef.current();
      })
      .finally(() => {
        if (mounted) {
          setCheckingImpact(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [deleteCollection, open, videofeedId]);

  const handleDelete = async () => {
    if (deleteInFlightRef.current || checkingImpact) {
      return;
    }

    deleteInFlightRef.current = true;
    setDeleting(true);
    try {
      await deleteVideofeed({
        videofeedId,
        confirm: true,
        deleteCollection,
      });
      switchModalRef.current();
    } catch (err) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notificationRef.current.showMessage(err?.response?.data?.message);
      } else {
        notificationRef.current.showMessage(NOTIFICATION.NOT_DELETED);
      }
      deleteInFlightRef.current = false;
      setDeleting(false);
      return;
    }

    getUserOrganization()
      .then((resp) => {
        dispatch(saveMyOrganization(resp.data));
      })
      .catch((error) => console.error(error));

    try {
      fetchAll?.();
    } catch (error) {
      console.error(error);
    }

    try {
      clearLocalStorageData?.();
    } catch (error) {
      console.error(error);
    }

    dispatch(getFoldersThunk({ page, limit, sortOption }));
    deleteInFlightRef.current = false;
    setDeleting(false);
  };

  useHotkeys('Enter', () => {
    if (open && !checkingImpact && !deleting) submitButtonRef?.current?.click();
  });

  return (
    <Modal open={open} onClose={switchModal} loading={checkingImpact || deleting}>
      <div className={styles.modalBody}>
        <div className={styles.modalTitle}>
          {deleteCollection
            ? hasReferenceTimelines
              ? 'Are you sure you want to delete this movie collection? This will also delete all reference timelines.'
              : 'Are you sure you want to delete this movie collection?'
            : `Are you sure you want to delete this ${deleteTargetLabel}?`}
        </div>
        {!!impactedComposers.length && (
          <>
            <div className={styles.modalSubtitle}>
              {deleteCollection
                ? 'Deleting this movie collection will also remove clips from these Online Composers.'
                : `Deleting this ${deleteTargetLabel} will also remove clips from these Online Composers.`}
            </div>
            <div className={styles.modalSubtitleSecondary}>
              Composers that only depend on this source will be deleted.
            </div>
            <div className={styles.namesList}>
              {impactedComposers.map((composer, index) => (
                <div key={`${composer}-${index}`} className={styles.nameItem}>
                  {composer}
                </div>
              ))}
            </div>
          </>
        )}
        <div className={styles.restoreDisclaimer}>
          Deleted content can be restored for 30 days from Deleted videofeeds.
        </div>

        <div className={styles.form}>
          <div className={styles.actionBtns}>
            <OutlinedButton onClick={handleDelete} disabled={checkingImpact || deleting} kind="default">
              <div ref={submitButtonRef}>Yes, I am sure</div>
            </OutlinedButton>

            <ContainedButton kind="purple" disabled={checkingImpact || deleting} onClick={switchModal}>
              No, cancel
            </ContainedButton>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteVideofeedModal;
